import type { ComponentType } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  useAbandonQuest,
  useAcceptQuest,
  useClaimQuest,
  useMyMissions,
  useOrderRareQuest,
  useQuests,
  useRecits,
  useRefuseQuest,
  useRerollQuest,
  useWorldMap,
  type QuestDto,
  type TavernStatusDto,
} from '@/api/hooks'
import { QuestCard } from '@/components/QuestCard'
import { TavernCheers } from '@/components/TavernCheers'
import { Card, GoldButton } from '@/components/game/ui'
import { BottleIcon, TreasureMapIcon, GoldIcon, RumIcon } from '@/components/game/GameIcons'
import { countdownLabel } from '@/lib/quests'
import { buildActiveQuestHints, type QuestHint } from '@/lib/questHints'

/**
 * La Taverne — tableau des quêtes gated derrière le bâtiment Taverne.
 * Quand la taverne n'est pas encore construite, affiche un teaser + CTA.
 * Quand elle est construite, affiche le bandeau de statut + les trois
 * sections du tableau (Proposées / En cours / À réclamer) avec reroll
 * par offre, tournée (L5+), et choix or/bois au claim.
 */
export function Tavern() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuests()
  const accept = useAcceptQuest()
  const refuse = useRefuseQuest()
  const claim = useClaimQuest()
  const reroll = useRerollQuest()
  const orderRare = useOrderRareQuest()
  const abandon = useAbandonQuest()

  // Indices « Où faire ça » : dérivés des données déjà en cache (aucune requête
  // dédiée). Undefined tant que le worldmap/missions chargent → pas d'indice, la
  // carte reste inchangée par ailleurs.
  const { data: world } = useWorldMap()
  const { data: missionsData } = useMyMissions()
  const { data: recitsData } = useRecits()
  const questHints: Record<number, QuestHint[]> =
    world && data
      ? buildActiveQuestHints(data.active, world.islands, missionsData?.missions ?? [], recitsData?.recits ?? [])
      : {}

  const [cheering, setCheering] = useState(false)
  const [rerollingId, setRerollingId] = useState<number | null>(null)

  // ── Teaser ──────────────────────────────────────────────────────────────────
  if (!isLoading && data && !data.tavern.built) {
    return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-[300px]">
        <div className="text-center py-12 flex flex-col items-center gap-6">
          <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300">La Taverne</p>
          <h1 className="font-display text-4xl sm:text-5xl text-sand-300">Construis ta taverne</h1>
          <p className="text-ocean-100/80 max-w-xl mx-auto">
            Le tableau des quêtes n'est accessible qu'une fois ta taverne bâtie. Rends-toi sur Mon île pour lancer la construction.
          </p>
          <GoldButton className="text-base px-6 py-2.5" onClick={() => navigate('/island')}>
            Construire la taverne sur Mon île
          </GoldButton>
        </div>
      </div>
    )
  }

  const tavern = data?.tavern
  const offered = data?.offered ?? []
  const active = data?.active ?? []
  const completed = data?.completed ?? []

  const canTournee = tavern != null && tavern.level >= tavern.tourneeMinLevel
  // Board full = no free offer slot; a tournée would 409 (board_full), so block it
  // up front rather than letting the click silently fail.
  const boardFull = tavern != null && offered.length >= tavern.offersCap
  // Active cap reached = accepting a TIMED offer would 409 (invalid_transition);
  // disable Accepter on those cards up front (event quests bypass the cap).
  const activeCapReached = tavern != null && tavern.activeCount >= tavern.activeCap
  const tourneeCountdown = countdownLabel(tavern?.tourneeAvailableAt ?? null)
  const tourneeDisabled = tourneeCountdown !== null || boardFull || orderRare.isPending
  // Why the button is unavailable — shown visibly (a title tooltip is invisible on mobile).
  const tourneeHint = tourneeCountdown !== null
    ? `Disponible ${tourneeCountdown}`
    : boardFull
      ? 'Tableau plein, libère un slot'
      : null

  function handleTournee() {
    orderRare.mutate(undefined, {
      onSuccess: () => {
        setCheering(true)
        toast.success('Tournée payée. Une offre rare t\'attend à la taverne !')
        window.setTimeout(() => setCheering(false), 1300)
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'La tournée a échoué.'),
    })
  }

  function handleReroll(id: number) {
    setRerollingId(id)
    reroll.mutate(id, {
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Le reroll a échoué.'),
      onSettled: () => setRerollingId(null),
    })
  }

  function handleAccept(id: number) {
    accept.mutate(id, {
      // Sans onError l'échec serveur (409 cap atteint, offre expirée…) était muet.
      onError: (err) => toast.error(err instanceof Error ? err.message : "L'acceptation a échoué."),
    })
  }

  return (
    <>
    <TavernCheers show={cheering} />
    <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-[300px] space-y-7">
      <div className="text-center py-4">
        <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-3">Carnet de bord</p>
        <h1 className="font-display text-4xl sm:text-5xl text-sand-300">La Taverne</h1>
        <p className="text-ocean-100/80 mt-2 max-w-2xl mx-auto">
          Le tableau des quêtes de ton île : on y affiche les affaires, on y paie les tournées.
        </p>
      </div>

      {isLoading ? (
        <p className="text-center text-sm text-ocean-300">Chargement…</p>
      ) : (
        <>
          {tavern && <TavernBanner tavern={tavern} eventActive={Math.max(0, active.length - tavern.activeCount)} />}

          <QuestSection
            title="Proposées"
            Icon={BottleIcon}
            quests={offered}
            empty="La mer est calme : aucune quête proposée pour l'instant."
            onAccept={handleAccept}
            onRefuse={(id) => refuse.mutate(id)}
            onReroll={handleReroll}
            rerollCost={tavern?.rerollCost ?? null}
            rerollingId={rerollingId}
            busy={accept.isPending || refuse.isPending}
            acceptBlocked={activeCapReached}
            activeCount={tavern?.activeCount}
            activeCap={tavern?.activeCap}
            extraHeader={
              canTournee ? (
                <div className="ml-auto flex flex-col items-end gap-1">
                  <button
                    onClick={handleTournee}
                    disabled={tourneeDisabled}
                    title={tourneeHint ?? 'Commander une quête rare'}
                    className="flex items-center gap-1.5 rounded-lg border border-rum-500/30 bg-ocean-900/40 text-ocean-50 font-semibold text-sm px-3 py-1.5 transition-colors hover:text-white hover:border-rum-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RumIcon className="w-4 h-4" />
                    <span className="tabular-nums">{tavern!.tourneeCostRhum}</span>
                    <span className="text-xs">Payer une tournée</span>
                  </button>
                  {tourneeHint && <span className="text-[11px] text-ocean-300">{tourneeHint}</span>}
                </div>
              ) : null
            }
          />
          <QuestSection
            title="En cours"
            Icon={TreasureMapIcon}
            quests={active}
            empty="Aucune quête en cours. Accepte-en une pour lever l'ancre."
            onAbandon={(id) => abandon.mutate(id)}
            busy={abandon.isPending}
            hintsByQuestId={questHints}
            onNavigate={navigate}
          />
          <QuestSection
            title="À réclamer"
            Icon={GoldIcon}
            quests={completed}
            empty="Aucune récompense à réclamer pour l'instant."
            onClaim={(id, resource) => claim.mutate({ id, resource })}
            busy={claim.isPending}
          />
        </>
      )}
    </div>
    </>
  )
}

// ── TavernBanner ─────────────────────────────────────────────────────────────

function TavernBanner({ tavern, eventActive }: { tavern: TavernStatusDto; eventActive: number }) {
  const nextOfferLabel = countdownLabel(tavern.nextOfferAt)

  return (
    <Card className="px-5 py-4 flex flex-wrap items-center gap-4 text-sm">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-display text-lg text-sand-300">Taverne niveau {tavern.level}</span>
        {/* The cap (activeCap, grows with the Tavern level) bounds BOARD quests only;
            chain/event quests are deliberately hors-quota, so we surface them on their
            own line — otherwise "4 / 5" next to a list of 6 reads like a bug. */}
        <span className="text-ocean-300 text-xs">
          Affaires de tableau : <span className="tabular-nums text-ocean-100">{tavern.activeCount} / {tavern.activeCap}</span>
        </span>
        {eventActive > 0 && (
          <span className="text-ocean-400 text-[11px]">
            + {eventActive} chaîne{eventActive > 1 ? 's' : ''} / événement{eventActive > 1 ? 's' : ''} · hors quota
          </span>
        )}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-ocean-300 text-xs">
          Tableau : <span className="tabular-nums text-ocean-100">{tavern.offersCap}</span> offre(s) max
        </span>
        {nextOfferLabel && (
          <span className="text-ocean-300 text-xs">
            Prochaine offre <span className="text-ocean-100">{nextOfferLabel}</span>
          </span>
        )}
      </div>
      {/* Reroll economics: cost climbs within the day (60→120→240→480, 4 max) then
          resets to 60 each day — so the player sees the way back down. */}
      <div className="flex flex-col gap-0.5 min-w-0">
        {tavern.rerollCost !== null ? (
          <span className="text-ocean-300 text-xs inline-flex items-center gap-1">
            Reroll d'une offre : <span className="tabular-nums text-ocean-100">{tavern.rerollCost}</span>
            <RumIcon className="w-3 h-3 text-sand-300" />
            <span className="text-ocean-400">· {tavern.rerollsUsedToday}/4 aujourd'hui</span>
          </span>
        ) : (
          <span className="text-ocean-300 text-xs">Reroll épuisé pour aujourd'hui</span>
        )}
        <span className="text-ocean-400 text-[11px]">Coût remis à zéro chaque jour.</span>
      </div>
    </Card>
  )
}

// ── QuestSection ──────────────────────────────────────────────────────────────

interface SectionProps {
  title: string
  Icon: ComponentType<{ className?: string }>
  quests: QuestDto[]
  empty: string
  onAccept?: (id: number) => void
  onRefuse?: (id: number) => void
  onClaim?: (id: number, resource?: 'gold' | 'wood') => void
  onReroll?: (id: number) => void
  onAbandon?: (id: number) => void
  rerollCost?: number | null
  rerollingId?: number | null
  busy?: boolean
  /** True when the tavern active cap is reached — timed offers become non-acceptables. */
  acceptBlocked?: boolean
  activeCount?: number
  activeCap?: number
  /** Optional element rendered right-aligned in the section header (e.g. tournée button). */
  extraHeader?: React.ReactNode
  /** Indices d'étape courante par quête (id → liste). Fournis à la section active. */
  hintsByQuestId?: Record<number, QuestHint[]>
  onNavigate?: (to: string) => void
}

function QuestSection({ title, Icon, quests, empty, onAccept, onRefuse, onClaim, onReroll, onAbandon, rerollCost, rerollingId, busy, acceptBlocked, activeCount, activeCap, extraHeader, hintsByQuestId, onNavigate }: SectionProps) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2.5">
        <h2 className="font-display text-2xl text-sand-100 flex items-center gap-2.5">
          <Icon className="w-6 h-6 text-sand-300" />
          {title}
          <span className="text-sm font-sans text-ocean-300">{quests.length}</span>
        </h2>
        {extraHeader}
      </div>
      {quests.length === 0 ? (
        <p className="text-sm text-ocean-300">{empty}</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {quests.map((q) => (
            <QuestCard
              key={q.id}
              quest={q}
              onAccept={onAccept}
              onRefuse={onRefuse}
              onClaim={onClaim}
              onReroll={onReroll}
              onAbandon={onAbandon}
              rerollCost={rerollCost}
              rerolling={rerollingId === q.id}
              busy={busy}
              acceptBlocked={acceptBlocked}
              activeCount={activeCount}
              activeCap={activeCap}
              hints={hintsByQuestId?.[q.id]}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </section>
  )
}
